import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  IconButton,
  Input,
  Text,
} from "@chakra-ui/react";
import Decimal from "decimal.js";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { Errors, PartNo, PriceRows, QuantityAndPriceMonthTable } from "../../contexts/types";
import { getFiscalMonthsString } from "../../utils/calculation";
import { formatNumber, getVisibleMonths, isPropsEqual, resolveDisplayPartNoByKey } from "../../utils/utils";
import { CustomNumberInput } from "./CustomNumberInput";

/**
 * 新製品製造シミュレーションのPartNo1件分（数量・単価入力グリッド + 価格帯入力UI + 集計表示）を表示するコンポーネントのProps
 */
interface Props {
  /** 対象PartNoのテーブルデータ */
  table: QuantityAndPriceMonthTable;
  /** map内でのインデックス（key用） */
  tableIndex: number;
  /** プロジェクト開始日（YYYY-MM-DD形式） */
  projectStartDate: string | null | undefined;
  /** EOL予定日（YYYY-MM-DD形式） */
  projectEndDate: string | null | undefined;
  /** 基準年度（開始年） */
  baseStartYear: number;
  /** 表示用通貨名 */
  currencyName: string;
  /** displayPartNoを含むPartNo一覧（画面表示用PartNo解決に使用、No.435対応） */
  partNoList: Pick<PartNo, "partNo" | "displayPartNo">[];
  /** 入力エラー情報 */
  errors: Errors;
  /** PartNoごとの数量合計（対応Aのキャッシュから取得した値） */
  totalQuantity: Decimal;
  /** PartNoごとの売上合計（対応Aのキャッシュから取得した値） */
  totalSales: Decimal;
  /** 価格帯行データ（全テーブル分） */
  priceRows: { id: string; rows: PriceRows[] }[];
  /** 価格帯行データの更新関数 */
  setPriceRows: React.Dispatch<React.SetStateAction<{ id: string; rows: PriceRows[] }[]>>;
  /** 数量行のラベル文言 */
  labelQuantity: string;
  /** 単価行のラベル文言 */
  labelPrice: string;
  /**
   * 数量/単価の入力変更を反映する
   * @param tableId テーブルID
   * @param year 年度
   * @param month 月キー
   * @param field 更新対象フィールド
   * @param value 更新値
   */
  handleInputChange: (
    tableId: string,
    year: number,
    month: number,
    field: "quantity" | "price",
    value: number | null
  ) => void;
  /** グラフの開始月を取得する（Input type="month"のmin用） */
  getGraphStartMonth: () => string;
  /** グラフの終了月を取得する（Input type="month"のmax用） */
  getGraphEndMonth: () => string;
}

/**
 * 新製品製造シミュレーションのPartNo1件分（数量・単価入力グリッド + 価格帯入力UI + 集計表示）を表示するコンポーネント
 * quantityAndPriceTables.map(...) から切り出した対応B（テーブル行のコンポーネント分割 + React.memo化）
 */
const QuantityAndPriceSection: React.FC<Props> = memo((props) => {
  const {
    table,
    tableIndex,
    projectStartDate,
    projectEndDate,
    baseStartYear,
    currencyName,
    partNoList,
    errors,
    totalQuantity,
    totalSales,
    priceRows,
    setPriceRows,
    labelQuantity,
    labelPrice,
    handleInputChange,
    getGraphStartMonth,
    getGraphEndMonth,
  } = props;

  const { t } = useTranslation();

  // console.info(`★レンダリング - 販売数量・販売単価`); // 再描画確認用

  const yearMonthList = getFiscalMonthsString(
    projectStartDate!,
    table.years[table.years.length - 1],
    false
  );

  /**
   * 指定された年・月がEOL予定日を超えているかを判定
   * @param year 年次（1年目、2年目など）
   * @param month 月次（1～12の数字）
   * @param baseYear 基準年度
   * @param startMonth プロジェクト開始月
   * @param endDate EOL予定日（YYYY-MM-DD形式）
   * @returns EOL予定日を超えている場合はtrue
   */
  const isAfterEOL = (
    year: number,
    month: number,
    baseYear: number,
    startMonth: number,
    endDate: string | null | undefined
  ): boolean => {
    // EOL予定日が設定されていない場合は非活性にしない
    if (!endDate) {
      return false;
    }

    // EOL予定日の年月を取得
    const eolYear = parseInt(endDate.split("-")[0], 10);
    const eolMonth = parseInt(endDate.split("-")[1], 10);

    // 現在の年度を計算
    // 4月～12月は当年度、1月～3月は前年度
    let actualYear: number;
    if (year === 1) {
      // 初年度の場合
      if (startMonth >= 4 && startMonth <= 12) {
        // 開始月が4～12月の場合
        actualYear = month >= 4 ? baseYear : baseYear + 1;
      } else {
        // 開始月が1～3月の場合
        actualYear = baseYear + 1;
      }
    } else {
      // 2年目以降
      actualYear = month >= 4 ? baseYear + year - 1 : baseYear + year;
    }

    // 年月を比較（YYYYMM形式で比較）
    const currentYearMonth = actualYear * 100 + month;
    const eolYearMonth = eolYear * 100 + eolMonth;

    return currentYearMonth > eolYearMonth;
  };

  /**
   * 数量/単価/金額グリッドの各セルを生成する
   * @param table テーブル情報
   * @param projectStartDate プロジェクト開始日
   * @param renderCell 各セルを描画するコールバック
   */
  const generateGridItems = (
    table: QuantityAndPriceMonthTable,
    projectStartDate: string | null | undefined,
    renderCell: (monthData: any, year: number, monthKey: number, index: number, isDisabled: boolean) => JSX.Element
  ): JSX.Element[] => {
    const startMonth = projectStartDate ? parseInt(projectStartDate.split("-")[1], 10) : 4;

    return table.years.flatMap((year) => {
      const months = table.data[year]?.months || {};
      const visibleMonthKeys = getVisibleMonths(year, months, startMonth);

      return visibleMonthKeys.map((monthKey, monthIndex) => {
        const monthData = months[monthKey];
        const actualMonth = monthData?.month || monthKey;
        // EOL予定日を超えているかを判定
        const isDisabled = isAfterEOL(year, actualMonth, baseStartYear, startMonth, projectEndDate);
        return renderCell(monthData, year, monthKey, monthIndex, isDisabled);
      });
    });
  };

  // 月算出処理
  const getMonth = (month: string, num: number): string => {
    if (!month) return "";
    const [y, m] = month.split("-").map(Number);
    const date = new Date(y, m - 1 + num, 1);
    const year = date.getFullYear();
    const newMonth = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${newMonth}`;
  };

  // 年月比較処理
  function isFutureMonth(from: string, to: string): boolean {
    const [fromYear, fromMonth] = from.split("-").map(Number);
    const [toYear, toMonth] = to.split("-").map(Number);

    if (toYear > fromYear) return true;
    if (toYear < fromYear) return false;

    return toMonth > fromMonth;
  }

  /**
   * 単価範囲入力（価格帯の期間×金額を複数行で入力するUI）を描画する
   * @param table テーブル情報
   */
  const renderSection = (table: QuantityAndPriceMonthTable) => {
    const matched = priceRows.find((item) => item.id === table.id);
    const rows = matched ? matched.rows : [];

    const addRow = (rowId: number) => {
      setPriceRows((prev) => {
        const target = prev.find((t) => t.id === table.id);
        if (!target) return prev;

        const last = target.rows[target.rows.length - 1];
        if (!last?.endMonth) return prev;
        const nextStart = getMonth(last.endMonth, 1);

        const newRow: PriceRows = {
          id: rowId + 1,
          displayStartMonth: nextStart,
          displayEndMonth: "",
          startMonth: nextStart,
          endMonth: "",
          value: 0,
        };

        const updatedRows = [...target.rows, newRow];

        return prev.map((t) =>
          t.id === table.id ? { ...t, rows: updatedRows } : t
        );
      });
    };

    const removeRow = (rowId: number) => {
      setPriceRows((prev) => {
        const target = prev.find((t) => t.id === table.id);
        if (!target) return prev;

        updateRow(rowId, "value", 0);

        const updatedRows = target.rows.filter((row) => row.id !== rowId);

        return prev.map((t) =>
          t.id === table.id ? { ...t, rows: updatedRows } : t
        );
      });
    };

    // 表示用年月を更新
    const handleDisplayMonthChange = (
      rowId: number,
      field: "startMonth" | "endMonth",
      value: string
    ) => {
      const displayField = field === "startMonth" ? "displayStartMonth" : "displayEndMonth";

      setPriceRows(prev =>
        prev.map(tableItem =>
          tableItem.id === table.id
            ? {
              ...tableItem,
              rows: tableItem.rows.map(r =>
                r.id === rowId
                  ? { ...r, [displayField]: value }
                  : r
              ),
            }
            : tableItem
        )
      );
    };

    const updateRow = (
      rowId: number,
      field: "startMonth" | "endMonth" | "value",
      value: string | number | null
    ) => {
      setPriceRows((prev) =>
        prev.map((tableRows) => {
          if (tableRows.id !== table.id) return tableRows;

          const targetRows = tableRows.rows.map((row) =>
            row.id === rowId ? { ...row, [field]: value } : row
          );

          const row = targetRows.find((r) => r.id === rowId);
          const index = targetRows.findIndex(r => r.id === rowId)

          const updatedRows = row ? [row] : [];

          if (row && field === "endMonth" && targetRows.length !== index + 1) {
            const nextRow = targetRows[index + 1];
            const month = getMonth(row.endMonth, 1);
            if (isFutureMonth(month, nextRow.startMonth)) {
              nextRow.startMonth = month;
              updatedRows.push(nextRow);
            }
          }

          if (row && field === "startMonth" && row.value !== 0) {
            const rowInfo = tableRows.rows.find((r) => r.id === rowId);

            if (rowInfo) {
              const prevRow: PriceRows = {
                id: 0,
                displayStartMonth: rowInfo.startMonth,
                displayEndMonth: getMonth(row.startMonth, -1),
                startMonth: rowInfo.startMonth,
                endMonth: getMonth(row.startMonth, -1),
                value: 0,
              };
              updatedRows.push(prevRow);
            }
          }

          if (row && field === "endMonth" && row.value !== 0 && targetRows.length === index + 1) {
            const rowInfo = tableRows.rows.find((r) => r.id === rowId);

            if (rowInfo) {
              const nextRow: PriceRows = {
                id: 0,
                displayStartMonth: getMonth(row.endMonth, 1),
                displayEndMonth: rowInfo.endMonth,
                startMonth: getMonth(row.endMonth, 1),
                endMonth: rowInfo.endMonth,
                value: 0,
              };
              updatedRows.push(nextRow);
            }
          }

          for (const updatedRow of updatedRows) {
            if (
              updatedRow?.startMonth &&
              updatedRow?.endMonth &&
              !(field === "endMonth" &&
                updatedRow?.value === 0 &&
                updatedRows.indexOf(updatedRow) === 0 &&
                targetRows[targetRows.length - 1].id === rowId) &&
              !(field === "startMonth" &&
                updatedRow?.value === 0 &&
                updatedRows.indexOf(updatedRow) === 0)
            ) {
              const [startY, startM] = updatedRow.startMonth.split("-").map(Number);
              const [endY, endM] = updatedRow.endMonth.split("-").map(Number);

              let fiscalY =
                startM >= 4 && startM <= 12
                  ? startY - baseStartYear + 1
                  : startY - baseStartYear;

              let endFiscalY =
                endM >= 4 && endM <= 12
                  ? endY - baseStartYear + 1
                  : endY - baseStartYear;

              let m = startM;

              while (fiscalY <= endFiscalY) {
                const months = table.data[fiscalY]?.months;
                if (months) {
                  const monthKey = Object.keys(months).find(
                    (key) => months[Number(key)]?.month === m
                  );
                  const endMnthKey = Object.keys(months).find(
                    (key) => months[Number(key)]?.month === endM
                  );
                  if (fiscalY < endFiscalY || Number(monthKey) <= Number(endMnthKey)) {
                    handleInputChange(table.id, fiscalY, Number(monthKey), "price", updatedRow.value);
                  }
                }

                if (m === 3) {
                  fiscalY++;
                }
                m++;

                if (m > 12) {
                  m = 1;
                }
              }
            }
          }
          return { ...tableRows, rows: targetRows };
        })
      );
    };

    return (
      rows.length > 0 && (
        <>
          <Box mb={4}>
            <Text fontWeight="bold" mb={2}>
              {labelPrice}
            </Text>
            {/* ヘッダ部分 */}
            <Grid templateColumns="150px 20px 150px 150px 80px" mb={2} alignItems="center" gap={3}>
              <GridItem>
                <Text>{t('features.InvestmentScreening.SimulationNewProduct.label.startYearMonth')}</Text>
              </GridItem>
              <GridItem />
              <GridItem>
                <Text>{t('features.InvestmentScreening.SimulationNewProduct.label.endYearMonth')}</Text>
              </GridItem>
              <GridItem>
                <Text>{t('features.InvestmentScreening.SimulationNewProduct.label.amount')}</Text>
              </GridItem>
              <GridItem />
            </Grid>
            {rows.map((row, index) => (
              <Grid
                key={row.id}
                templateColumns="150px 20px 150px 150px 80px"
                alignItems="center"
                mb={3}
                gap={3}
              >
                <GridItem>
                  <Input
                    type="month"
                    width="150px"
                    min={getGraphStartMonth()}
                    max={row.endMonth || getGraphEndMonth()}
                    value={row.displayStartMonth}
                    onChange={(e) => (handleDisplayMonthChange(row.id, "startMonth", e.target.value))}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (row.startMonth !== value && value >= getGraphStartMonth() && value <= (row.endMonth || getGraphEndMonth())) {
                        updateRow(row.id, "startMonth", e.target.value);
                      } else {
                        handleDisplayMonthChange(row.id, "startMonth", row.startMonth);
                      }
                    }}
                    isDisabled={index > 0}
                  />
                </GridItem>
                <GridItem justifySelf="center"><Text>～</Text></GridItem>
                <GridItem>
                  <Input
                    type="month"
                    width="150px"
                    min={row.startMonth || getGraphStartMonth()}
                    max={getGraphEndMonth()}
                    value={row.displayEndMonth}
                    onChange={(e) => (handleDisplayMonthChange(row.id, "endMonth", e.target.value))}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (row.endMonth !== value && value >= (row.startMonth || getGraphStartMonth()) && value <= getGraphEndMonth()) {
                        updateRow(row.id, "endMonth", e.target.value);
                      } else {
                        handleDisplayMonthChange(row.id, "endMonth", row.endMonth);
                      }
                    }}
                  />
                </GridItem>

                <GridItem>
                  <CustomNumberInput
                    value={row.value || 0}
                    onChange={() => { }}
                    onBlur={(value) => updateRow(row.id, "value", value)}
                    max={9999999999}
                    min={0}
                    decimalPlaces={3}
                  />
                </GridItem>
                <GridItem>
                  {index === rows.length - 1 && (
                    <IconButton
                      aria-label="Add"
                      icon={<FaPlusCircle />}
                      colorScheme="blue"
                      size="sm"
                      mr={2}
                      onClick={() => addRow(row.id)}
                      isDisabled={!row.endMonth}
                    />
                  )}
                  {index === rows.length - 1 && index !== 0 && (
                    <IconButton
                      aria-label="Remove"
                      icon={<FaCircleMinus />}
                      colorScheme="red"
                      size="sm"
                      onClick={() => removeRow(row.id)}
                    />
                  )}
                </GridItem>
              </Grid>
            ))}
          </Box>
        </>
      )
    );
  };

  return (
    <Accordion allowToggle key={tableIndex}>
      <AccordionItem>
        <h2>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              {resolveDisplayPartNoByKey(table.partNo, partNoList)}
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>
          <Box
            className="simulation-scrollable-container"
            key={table.id}
            id={table.id}
            maxW="79.5vw"
            mx="auto"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="md"
            overflowX="auto"
            height="fit-content"
            mb={6}
          >
            <Box
              p={4}
            >
              {/* 単価範囲入力 */}
              {renderSection(table)}
              <Grid
                templateColumns={`100px repeat(${yearMonthList.length}, 150px)`} // 各列の幅を固定
                gap={4}
              >
                {/* ヘッダー */}
                <GridItem>
                  <Text fontWeight="bold">{t('features.InvestmentScreening.SimulationNewProduct.label.item')}</Text>
                </GridItem>
                {yearMonthList?.map((yearMonth: any) => {
                  const { year, month } = yearMonth;
                  return (
                    <Text textAlign="center" fontWeight="bold" key={`header-${year}-${month}`}>
                      {t('common.dateFormat.yearMonth', { year, month })}
                    </Text>)
                })}

                {/* 数量行 */}
                <GridItem>
                  <Text>{labelQuantity}</Text>
                </GridItem>
                {generateGridItems(table, projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                  <GridItem key={`quantity-${table.id}-${year}-${monthKey}`}>
                    <CustomNumberInput
                      value={monthData?.quantity || 0}
                      isDisabled={isDisabled}
                      onChange={(value) =>
                        handleInputChange(table.id, year, monthKey, "quantity", value)
                      }
                      max={9999999999}
                      min={0}
                      decimalPlaces={0}
                    />
                  </GridItem>
                ))}
                {/* 単価行 */}
                <GridItem>
                  <Text>{labelPrice}</Text>
                </GridItem>
                {generateGridItems(table, projectStartDate, (monthData, year, monthKey, index, isDisabled) => (
                  <GridItem key={`price-${table.id}-${year}-${monthKey}`}>
                    <FormControl isInvalid={!!errors[`partNoTables-${table.id}-${year}-${monthKey}-price`]?.isError}>
                      <CustomNumberInput
                        value={monthData?.price || 0}
                        isDisabled={isDisabled}
                        onChange={(value) =>
                          handleInputChange(table.id, year, monthKey, "price", value)
                        }
                        max={9999999999}
                        min={0}
                        decimalPlaces={3}
                      />
                      {errors[`partNoTables-${table.id}-${year}-${monthKey}-price`] && (
                        <FormErrorMessage>{errors[`partNoTables-${table.id}-${year}-${monthKey}-price`].errorMessage}</FormErrorMessage>
                      )}
                    </FormControl>
                  </GridItem>
                ))}
                {/* 金額行 */}
                <GridItem>
                  <Text>{t('features.InvestmentScreening.SimulationNewProduct.label.productSales')}</Text>
                </GridItem>
                {generateGridItems(table, projectStartDate, (monthData, year, monthKey) => {
                  const sales = new Decimal(monthData?.quantity || 0).times(new Decimal(monthData?.price || 0));
                  return (
                    <GridItem key={`sales-${table.id}-${year}-${monthKey}`}>
                      <Text textAlign="right" mr={4}>{formatNumber(sales, currencyName, 3)}</Text>
                    </GridItem>
                  );
                })}
              </Grid>
            </Box>
            <Box p={4}>
              <Flex>
                <Text fontWeight="bold">
                  {t('features.InvestmentScreening.SimulationNewProduct.label.totalQuantity')}:{" "}
                  {formatNumber(
                    totalQuantity,
                    currencyName
                  )}
                </Text>
              </Flex>
              <Flex mt={4}>
                <Text fontWeight="bold">
                  {t('features.InvestmentScreening.SimulationNewProduct.label.totalAmount')}:{" "}
                  {formatNumber(totalSales, currencyName, 3)} {currencyName}
                </Text>
              </Flex>
            </Box>
          </Box>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
});

QuantityAndPriceSection.displayName = "QuantityAndPriceSection";

export default QuantityAndPriceSection;
